import { IonPage, IonContent, IonLabel, isPlatform, IonImg, IonButton } from '@ionic/react';
import { observer } from 'mobx-react';
import React from 'react';
import { CameraPreview } from '@capacitor-community/camera-preview';
import WebCam from 'react-webcam';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { t } from 'i18next';

import Step, { WorkflowProps } from './Step';
import '../../theme/pages/packageCapture.css';
import { Redirect } from 'react-router-dom';
import { PathName } from '../../commons/Constants';
import Utils from '../../utils/Utils';
import { ScreenType } from '../../commons/Constants';
import GreetlyIcon from '../../components/GreetlyIcon';

type State = {
  shouldRedirectToUnknownError: boolean;
  shouldShowWarning: boolean;
  shouldShowCamera: boolean;
  isReviewRequired: boolean;
  imageBase64: string | null;
};

class PhotoCapture extends Step<WorkflowProps, State> {
  state: State = {
    shouldRedirectToUnknownError: false,
    shouldShowWarning: false,
    shouldShowCamera: false,
    isReviewRequired: this.props.node.data.is_review_before_submission === true,
    imageBase64: null,
  };

  webCamRef = React.createRef<WebCam & HTMLVideoElement>();

  async componentDidMount() {
    if (Utils.isNative()) {
      this.startNativeCamera();
    } else {
      this.setupMediaDevice()
        .then(() => {
          this.setState({ shouldShowCamera: true });
        })
        .catch(() => {
          this.setState({ shouldShowWarning: true });
        });
    }
  }

  async componentWillUnmount() {
    if (Utils.isNative()) {
      const root = document.documentElement;
      this.stopNativeCamera();
      root.style.setProperty('--ion-background-color', 'var(--greetly-background-color)');
    }
  }

  startNativeCamera() {
    let width: number | undefined = undefined;
    let height: number | undefined = undefined;
    if (isPlatform('ios')) {
      if (
        ScreenOrientation.type === ScreenOrientation.ORIENTATIONS.LANDSCAPE ||
        ScreenOrientation.type === ScreenOrientation.ORIENTATIONS.LANDSCAPE_PRIMARY ||
        ScreenOrientation.type === ScreenOrientation.ORIENTATIONS.LANDSCAPE_SECONDARY
      ) {
        width = window.screen.height;
        height = window.screen.width;
      }
    }

    CameraPreview.start({
      width: width,
      height: height,
      position: 'rear',
      toBack: true,
      disableExifHeaderStripping: window.innerWidth > window.innerHeight,
    }).then(() => {
      const root = document.documentElement;
      root.style.setProperty('--ion-background-color', 'transparent');

      this.setState({ shouldShowCamera: true });
    });
  }

  stopNativeCamera() {
    return CameraPreview.stop();
  }

  restartNativeCamera() {
    this.stopNativeCamera().then(() => {
      this.startNativeCamera();
    });
  }

  _setImageClosePage(imageBase64: string) {
    const { workflowStore } = this.props.appStore;
    workflowStore.setPackageImageBase64(imageBase64);
    workflowStore.goToNextNode();
  }

  private fixAndroidLandscapeImage(base64: string): Promise<string> {
    return new Promise((resolve) => {
      if (!isPlatform('android') || window.innerWidth <= window.innerHeight) {
        resolve(base64);
        return;
      }

      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        if (ctx) {
          ctx.translate(0, canvas.height);
          ctx.scale(1, -1);
          ctx.drawImage(img, 0, 0);
          const newBase64 = canvas.toDataURL('image/jpeg', 1.0).split(',')[1];
          resolve(newBase64);
        } else {
          resolve(base64);
        }
      };
      
      img.src = 'data:image/jpeg;base64,' + base64;
    });
  }

  takePicture = () => {
    let _finishWithImage = (imageBase64: string) => {
      Utils.finishWithImage(
        imageBase64,
        this.state.isReviewRequired,
        (resizedImage) => this._setImageClosePage(resizedImage),
        (resizedImage) => {
          this.stopNativeCamera();
          this.setState({ imageBase64: resizedImage });
        }
      );
    };

    if (Utils.isNative()) {
      CameraPreview.capture({})
        .then((result: { value: string }) => {
          return this.fixAndroidLandscapeImage(result.value);
        })
        .then((fixedBase64: string) => {
          _finishWithImage(fixedBase64);
        })
        .catch(() => {
          Utils.showErrorToast('An error has occurred');
        });
    } else {
      const imageBase64Url = this.webCamRef.current!.getScreenshot();
      if (imageBase64Url !== null) {
        const imageBase64 = imageBase64Url.substring(imageBase64Url.lastIndexOf(',') + 1, imageBase64Url.length);
        _finishWithImage(imageBase64);
      } else {
        this.setState({ shouldRedirectToUnknownError: true });
      }
    }
  };

  retakePhoto() {
    this.setState({ imageBase64: null });

    if (Utils.isNative()) {
      this.startNativeCamera();
    } else {
      this.setupMediaDevice()
        .then(() => {
          this.setState({ shouldShowCamera: true });
        })
        .catch(() => {
          this.setState({ shouldShowWarning: true });
        });
    }
  }

  private setupMediaDevice = () => {
    return navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', height: { ideal: 4096 }, width: { ideal: 2160 } },
    });
  };

  render() {
    if (!Utils.isNative()) {
      if (this.state.shouldRedirectToUnknownError) {
        return <Redirect to={PathName.UNKNOWN_ERROR} />;
      }
    }

    const { locationStore } = this.props.appStore;

    return (
      <IonPage className="packagecapture">
        <IonContent scroll-y="false">
          {this.state.shouldShowWarning ? (
            <>
              <div className="warning">
                {/* @TODO styling */}
                Photo is required. Please allow the app to access the camera and try again.
              </div>
            </>
          ) : (
            <>
              {this.state.isReviewRequired && this.state.imageBase64 != null ? (
                <div className="photo-container">
                  <div>
                    <IonImg className="photo-image" src={'data:image/jpeg;base64, ' + this.state.imageBase64} />
                    <IonButton
                      className="generic-button"
                      onClick={() => this._setImageClosePage(this.state.imageBase64!)}
                      style={{ position: 'absolute', bottom: 0, right: '10%' }}
                    >
                      <div>{t('next')}</div>
                    </IonButton>
                    <IonButton
                      className="generic-button-cancel"
                      onClick={() => this.retakePhoto()}
                      style={{ position: 'absolute', bottom: 0, left: '10%' }}
                    >
                      {this.props.appStore.screenType === ScreenType.Phone && <GreetlyIcon iconName="rotate-left" />}
                      {this.props.appStore.screenType === ScreenType.Tablet && 'RE-TAKE'}
                    </IonButton>
                  </div>
                </div>
              ) : (
                this.state.shouldShowCamera && (
                  <>
                    {!Utils.isNative() && (
                      <div className="cam-cont">
                        <WebCam
                          className="cam-prev"
                          ref={this.webCamRef}
                          audio={false}
                          screenshotFormat="image/jpeg"
                          videoConstraints={{
                            facingMode: 'environment',
                          }}
                        ></WebCam>
                      </div>
                    )}
                    <div className="capture-container">
                      {
                        <div>
                          <IonLabel className="prev-text">
                            {locationStore.tabletTerminologies.packageBlockTitle}
                          </IonLabel>
                          <button className="capture-button" onClick={() => this.takePicture()} />
                        </div>
                      }
                    </div>
                  </>
                )
              )}
            </>
          )}
        </IonContent>
      </IonPage>
    );
  }
}

export default observer(PhotoCapture);
